// @flow

import React, { memo } from 'react';

import { translate } from '../../../base/i18n';
import {
    IconStartScreenShare
} from '../../../base/icons';
import { OverflowMenuItem } from '../../../base/toolbox/components';
import { SharedDocumentButton } from '../../../etherpad';
import { GenericIFrameButton } from '../../../genericiframe';
import ShareAudioButton from '../../../screen-share/components/web/ShareAudioButton';
import SharedVideoButton from '../../../shared-video/components/web/SharedVideoButton';
import { isMobileBrowser } from '../../../base/environment/utils';

/* global interfaceConfig */

type Props = {

    // Sharing state
    _shareVideoState: boolean,
    _screensharing: boolean,
    _whiteBoardvisible: boolean,
    _livePadVisible: boolean,
    _screenShareVisible: boolean,
    _isLocalWhiteboard: boolean,
    _isLocalEtherpad: boolean,
    _isOwnSharingVideo: boolean,
    _isModerator: boolean,
    _localParticipantID: string,
    _isWhiteBoardID: ?string,
    _isetherPadId: ?string,
    desktopApp: boolean,
    desktopSharingEnabled: boolean,
    _desktopSharingDisabledTooltipKey: boolean,

    onToggleScreenshare: Function,
    t: Function,
};

/**
 * Renders the share-flow menu items (screen, whiteboard, etherpad, video, audio).
 * Wrapped in React.memo — only re-renders when sharing state actually changes.
 */
const ShareMenuContent = memo(({
    _shareVideoState,
    _screensharing,
    _whiteBoardvisible,
    _livePadVisible,
    _screenShareVisible,
    _isLocalWhiteboard,
    _isLocalEtherpad,
    _isOwnSharingVideo,
    _isModerator,
    _localParticipantID,
    _isWhiteBoardID,
    _isetherPadId,
    desktopApp,
    desktopSharingEnabled,
    _desktopSharingDisabledTooltipKey,
    onToggleScreenshare,
    t
}: Props) => {
    const shouldShowButton = name => {
        const buttons = new Set(interfaceConfig.TOOLBAR_BUTTONS);

        return buttons.has(name);
    };

    const isDesktopSharingButtonVisible = () => !isMobileBrowser() && (desktopSharingEnabled || _desktopSharingDisabledTooltipKey);;

    const renderDesktopSharingButton = () => {
        if (!isDesktopSharingButtonVisible()) {
            return null;
        }

        return (
            <OverflowMenuItem
                accessibilityLabel = { t('toolbar.accessibilityLabel.shareYourScreen') }
                disabled = { !desktopSharingEnabled }
                icon = { IconStartScreenShare }
                iconId = 'share-desktop'
                key = 'desktop'
                onClick = { onToggleScreenshare }
                text = { _screensharing ? t('toolbar.stopScreenSharing') : t('toolbar.startScreenSharing') } />
        );
    };

    const buttons = [];

    const someoneElseSharingWhiteboard = _isWhiteBoardID && _isWhiteBoardID !== _localParticipantID;
    const someoneElseSharingLivePad = _isetherPadId && _isetherPadId !== _localParticipantID;
    const someoneElseSharingVideo = _shareVideoState && !_isOwnSharingVideo;

    // If LOCAL USER is sharing something, show only the stop button for that share type
    if (_screensharing) {
        const desktopBtn = renderDesktopSharingButton();

        if (desktopBtn) {
            buttons.push(desktopBtn);
        }

        return buttons;
    }

    if (_isLocalWhiteboard) {
        buttons.push(
            <GenericIFrameButton
                key = 'genericiframe'
                showLabel = { true } />
        );

        return buttons;
    }

    if (_isLocalEtherpad) {
        buttons.push(
            <SharedDocumentButton
                key = 'etherpad'
                showLabel = { true } />
        );

        return buttons;
    }

    if (_shareVideoState && _isOwnSharingVideo) {
        buttons.push(
            <SharedVideoButton
                key = 'sharedvideo'
                showLabel = { true } />
        );

        return buttons;
    }

    // LOCAL USER is not sharing — show available options
    if (shouldShowButton('desktop') && _screenShareVisible) {
        const desktopBtn = renderDesktopSharingButton();

        if (desktopBtn) {
            buttons.push(desktopBtn);
        }
    }

    if ((shouldShowButton('whiteboard') || _whiteBoardvisible) && _isModerator && !someoneElseSharingWhiteboard) {
        buttons.push(
            <GenericIFrameButton
                key = 'genericiframe'
                showLabel = { true } />
        );
    }

    if ((shouldShowButton('livepad') || _livePadVisible) && _isModerator && !someoneElseSharingLivePad) {
        buttons.push(
            <SharedDocumentButton
                key = 'etherpad'
                showLabel = { true } />
        );
    }

    if (shouldShowButton('sharedvideo') && _isModerator && !someoneElseSharingVideo) {
        buttons.push(
            <SharedVideoButton
                key = 'sharedvideo'
                showLabel = { true } />
        );
    }

    if (shouldShowButton('shareaudio') && !desktopApp && !isMobileBrowser() && _screenShareVisible) {
        buttons.push(
            <ShareAudioButton
                key = 'shareaudio'
                showLabel = { true } />
        );
    }

    return buttons;
});

ShareMenuContent.displayName = 'ShareMenuContent';

export default translate(ShareMenuContent);
